/*
 * File: src/pages/OutstandingOrders.jsx
 * Purpose: Main Outstanding Orders page for the all-reps view.
 * Handles fetching, filtering, sorting, pagination, and search for the table.
 */

import React from 'react';

import ClearIcon from '@mui/icons-material/Clear';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useInterval } from '@tzmedical/react-hooks';

import axios from '../axiosClient.js';
import Alert from '../components/common/Alert.jsx';
import PageLoading from '../components/common/PageLoading.jsx';
import ExportOutstandingOrders from '../components/OutstandingOrders/ExportOutstandingOrders.jsx';
import OutstandingOrdersTable from '../components/OutstandingOrders/OutstandingOrdersTable.jsx';

const REFRESH_INTERVAL_MS = 300_000;
const ROWS_PER_PAGE_OPTIONS = [50, 250, 1000];

function OutstandingOrders() {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [ordersError, setOrdersError] = React.useState(null);
  const [searchText, setSearchText] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [sortBy, setSortBy] = React.useState('OrderDate');
  const [sortDir, setSortDir] = React.useState('desc');

  const fetchOrders = React.useCallback(async () => {
    try {
      const { data } = await axios({
        method: 'GET',
        url: '/api/outstanding-orders/all-reps',
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          search: searchText.trim() || undefined,
          showShippedOrders: true,
          sortBy,
          sortDir,
        },
      });

      setOrders(data.orders);
      setTotalItems(data.total);
      setOrdersError(null);
      setLoading(false);
    } catch (err) {
      setOrdersError(err.response?.data?.error || err.message);
      setLoading(false);
    }
  }, [page, rowsPerPage, searchText, sortBy, sortDir]);

  const handleSort = React.useCallback(
    (nextSortBy) => {
      setPage(0);
      setLoading(true);

      if (nextSortBy === sortBy) {
        setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return;
      }

      setSortBy(nextSortBy);
      setSortDir('asc');
    },
    [sortBy]
  );

  const handleSearchChange = React.useCallback((event) => {
    setSearchText(event.target.value);
    setPage(0);
    setLoading(true);
  }, []);

  const handleClearSearch = React.useCallback(() => {
    setSearchText('');
    setPage(0);
    setLoading(true);
  }, []);

  const handleChangePage = React.useCallback((_, newPage) => {
    setPage(newPage);
    setLoading(true);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setLoading(true);
  }, []);

  useInterval(fetchOrders, REFRESH_INTERVAL_MS, loading && !ordersError);

  const content = React.useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ mt: 4, mb: 4 }}>
          <PageLoading />
        </Box>
      );
    }

    if (ordersError) {
      return (
        <Alert
          data-cy="outstanding-orders-error"
          message={ordersError}
          setMessage={setOrdersError}
          level="error"
        />
      );
    }

    return (
      <>
        <OutstandingOrdersTable
          orders={orders}
          selectedRep="all-reps"
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
        />
        <TablePagination
          data-cy="outstanding-orders-bottom-pagination"
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      </>
    );
  }, [
    loading,
    orders,
    totalItems,
    rowsPerPage,
    page,
    handleChangePage,
    handleChangeRowsPerPage,
    ordersError,
    sortBy,
    sortDir,
    handleSort,
  ]);

  return (
    <Box sx={{ mx: '0.25rem', mb: '0.25rem', mt: '1rem', width: '100%' }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          mb: 2,
          gap: 2,
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Typography variant="h4" data-cy="outstanding-orders-title">
            Outstanding Orders
          </Typography>
          <TextField
            size="small"
            label="Search"
            placeholder="Shipped, partial, invoice, order date, rep, item, PO"
            value={searchText}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      edge="end"
                      size="small"
                      onClick={handleClearSearch}
                      data-cy="outstanding-orders-search-clear"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            sx={{ minWidth: 320 }}
            data-cy="outstanding-orders-search"
          />
          <ExportOutstandingOrders searchText={searchText} />
        </Stack>
        {!loading && !ordersError && (
          <TablePagination
            data-cy="outstanding-orders-top-pagination"
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
          />
        )}
      </Stack>
      {content}
    </Box>
  );
}
export default React.memo(OutstandingOrders);
